import React, { useState, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { ToastError, ToastSuccess } from "../../../UiComponents/Toaster/Toast";

const formatNum = (value) =>
  value === undefined || value === null || value === ""
    ? "-"
    : Number(value).toLocaleString("en-IN");

/**
 * Reusable bulk-upload dialog for the payable screens (User Payable, Branch
 * Payable, ...). All screen-specific behaviour is passed via props so the flow
 * (download template -> verify -> show per-row issues -> create valid rows) is
 * defined once.
 *
 * Props:
 *  - open, setOpen, onSuccess
 *  - title            e.g. "Bulk Upload User Payable"
 *  - templateHeaders  array of header strings (must match the backend exactly)
 *  - templateFileName downloaded template file name
 *  - verifyService    (formData) => Promise
 *  - commitService    (formData) => Promise
 */
const BulkUploadPayableDialog = ({
  open,
  setOpen,
  onSuccess,
  title,
  templateHeaders = [],
  templateFileName = "Bulk_Upload_Template.xlsx",
  verifyService,
  commitService,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [verificationResults, setVerificationResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const savingRef = useRef(false); // a second click before the screen re-renders must not send the file twice

  const { readyCount, issuesCount } = useMemo(() => {
    let ready = 0;
    let issues = 0;
    verificationResults.forEach((r) => (r.error ? issues++ : ready++));
    return { readyCount: ready, issuesCount: issues };
  }, [verificationResults]);

  const handleClose = () => {
    setOpen(false);
    setSelectedFile(null);
    setVerificationResults([]);
    setLoading(false);
    setSaveLoading(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setVerificationResults([]);
    }
    event.target.value = "";
  };

  const downloadSampleExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Template");

    worksheet.columns = templateHeaders.map((header) => ({
      header,
      key: header,
      width: header === "Policy Number" ? 25 : 14,
    }));

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFF00" },
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, templateFileName);
  };

  const handleVerify = async () => {
    if (!selectedFile) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await verifyService(formData);
      if (response && !response.error) {
        const data = response.data || [];
        // Header-level error comes back as a single object without a row number.
        if (
          data.length === 1 &&
          data[0].error &&
          data[0].rowNumber === undefined
        ) {
          ToastError(data[0].message || "Invalid Excel headers");
          setVerificationResults([]);
        } else if (!data.length) {
          ToastError("No data rows found in the uploaded file.");
          setVerificationResults([]);
        } else {
          setVerificationResults(data);
          ToastSuccess("File verified successfully");
        }
      } else {
        ToastError(response.message || "Verification failed");
      }
    } catch (error) {
      console.error("Verification error:", error);
      ToastError("Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedFile) return;

    if (readyCount === 0) {
      ToastError("No valid rows to create. Please fix the errors and re-upload.");
      return;
    }

    if (savingRef.current) return;
    savingRef.current = true;
    setSaveLoading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await commitService(formData);
      if (response && !response.error) {
        // Header-invalid path returns { success: false, message }
        if (response.data && response.data.success === false) {
          ToastError(response.data.message || "Upload failed");
          return;
        }
        const success = response.data?.success || 0;
        const failed = response.data?.failed || 0;
        ToastSuccess(`${success} policies updated successfully!`);
        if (failed > 0) {
          ToastError(`${failed} rows were skipped due to errors.`);
        }
        if (onSuccess) onSuccess();
        handleClose();
      } else {
        ToastError(response.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      ToastError("Upload failed");
    } finally {
      savingRef.current = false;
      setSaveLoading(false);
    }
  };

  const hasResults = verificationResults.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 3 }}>
        {!hasResults ? (
          <>
            <Box
              component="label"
              sx={{
                border: "2px dashed #E4E7EC",
                borderRadius: "8px",
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                backgroundColor: "#F9FAFB",
                cursor: "pointer",
                "&:hover": { backgroundColor: "#F2F4F7" },
              }}
            >
              <input
                type="file"
                hidden
                accept=".xlsx, .xls"
                onChange={handleFileChange}
              />
              <CloudUploadIcon sx={{ fontSize: 48, color: "#667085" }} />
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedFile
                    ? selectedFile.name
                    : "Click to upload Excel file"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Only .xlsx and .xls files are supported
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: "#475467", mb: 1 }}>
                Required columns (in order):
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {templateHeaders.map((h) => (
                  <Chip key={h} label={h} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
              <Button
                startIcon={<FileDownloadIcon />}
                onClick={downloadSampleExcel}
                sx={{
                  textTransform: "none",
                  color: "#667085",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "#1D2939",
                  },
                }}
              >
                Download Template
              </Button>
            </Box>
          </>
        ) : (
          <>
            {/* Summary */}
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 200,
                  border: "1px solid #D0D5DD",
                  borderRadius: "8px",
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <CheckCircleOutlineIcon sx={{ color: "#10B981" }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {readyCount}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    rows ready to create
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 200,
                  border: "1px solid",
                  borderColor: issuesCount > 0 ? "#FDA29B" : "#D0D5DD",
                  backgroundColor: issuesCount > 0 ? "#FFFbfa" : "inherit",
                  borderRadius: "8px",
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <ErrorOutlineIcon
                  sx={{ color: issuesCount > 0 ? "#E50000" : "#98A2B3" }}
                />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {issuesCount}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    rows with issues
                  </Typography>
                </Box>
              </Box>
            </Box>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ maxHeight: 420, borderRadius: "8px", overflow: "auto" }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "Row",
                      "Policy Number",
                      "OD %",
                      "OD Amt",
                      "TP %",
                      "TP Amt",
                      "Net %",
                      "Net Amt",
                      "Total",
                      "Status",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontWeight: "bold",
                          backgroundColor: "#f8f9fa",
                          color: "#475467",
                          borderBottom: "2px solid #eaecf0",
                          py: 1.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {verificationResults.map((result, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: result.error ? "#FFF5F5" : "inherit",
                        "&:hover": { backgroundColor: "#f9fafb" },
                      }}
                    >
                      <TableCell sx={{ py: 1.25 }}>{result.rowNumber}</TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        {result.policyNumber || "-"}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        {formatNum(result.odPercentage)}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        {formatNum(result.odAmount)}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        {formatNum(result.tpPercentage)}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        {formatNum(result.tpAmount)}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        {formatNum(result.netPercentage)}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        {formatNum(result.netAmount)}
                      </TableCell>
                      <TableCell sx={{ py: 1.25, fontWeight: 600 }}>
                        {formatNum(result.total)}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.25,
                          color: result.error ? "error.main" : "success.main",
                          fontWeight: 600,
                          minWidth: 180,
                        }}
                      >
                        {result.error
                          ? result.message || result.status
                          : result.status}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {issuesCount > 0 && (
              <Typography variant="body2" sx={{ mt: 1.5, color: "#B42318" }}>
                Rows with issues will be skipped. Only the {readyCount} valid
                row(s) will be created.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, px: 3 }}>
        {hasResults && (
          <Button
            onClick={() => setVerificationResults([])}
            variant="outlined"
            sx={{ borderRadius: "8px", textTransform: "none", px: 3, mr: "auto" }}
          >
            Re-upload
          </Button>
        )}
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
        >
          {hasResults ? "Cancel" : "Close"}
        </Button>
        {!hasResults ? (
          <Button
            variant="contained"
            disabled={!selectedFile || loading}
            onClick={handleVerify}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              px: 3,
              backgroundColor: "#008CBA",
              "&:hover": { backgroundColor: "#007199" },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Verify File"
            )}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={saveLoading || readyCount === 0}
            onClick={handleSave}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              px: 3,
              backgroundColor: "#10B981",
              "&:hover": { backgroundColor: "#059669" },
            }}
          >
            {saveLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              `Create (${readyCount})`
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadPayableDialog;
